import React, { useContext, useMemo } from 'react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Log from '../../controllers/log';
import { useApi } from 'services/api';
import Cookies from 'universal-cookie';
import Cabecario from '../../components/util/cabecario/.mixins';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  List,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { Filters } from './filters';
import './styles.css';
import { CloudUpload } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { AuthContext } from 'contexts/AuthContext';

type ContactOption = {
  cdGep: number;
  nmGep: string;
};

const tenMegaBytes = 10_000_000;
const contentMaxLength = 1000;

const byteFormater = Intl.NumberFormat('en', {
  notation: 'compact',
  style: 'unit',
  unit: 'byte',
  unitDisplay: 'narrow'
});

const acceptedFileTypes =
  'text/plain, application/vnd.ms-excel, application/msword, png, jpg, jpeg, txt, pdf, xls, xlsx, doc, docx, csv';
const acceptedFiles = '.png, .jpg, .jpeg, .txt, .pdf, .xls, .xlsx, .doc, .docx, .csv';

function formatContactLabel({ cdGep, nmGep }: ContactOption) {
  if (cdGep === 0) return 'Equipe Tok&Stok';

  return `Grupo ${cdGep} - ${nmGep}`;
}

function getCurrentUser() {
  const cookies = new Cookies();
  const personId = cookies.get('User-Code');
  return Number(personId);
}

export default function Contact() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [fileList, setFileList] = useState<File[] | null>(null);
  const [selectedContact, setSelectContact] = useState<number[]>([0]);
  const [emailOptions, setEmailOptions] = useState<ContactOption[]>([]);
  const [errorMessage, setErrorMessage] = useState<Record<string, string | undefined>>({});
  const [isSendingEmail, setSendingEmail] = useState<boolean>();
  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>();
  const [personId, setPersonId] = useState(getCurrentUser);

  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [failedMessage, selectedFailedMessage] = useState<string | undefined>();

  const inputRef = React.useRef<HTMLInputElement>(null);
  const {
    user: { role }
  } = useContext(AuthContext);

  function handleChangeSupplier(selectedPerson: number) {
    if (selectedPerson === 0) {
      setSuccessMessage('Seleção de fornecedor removida!');
      setTimeout(() => setSuccessMessage(undefined), 1000);
      return setPersonId(getCurrentUser);
    }

    setPersonId(selectedPerson);
    setSuccessMessage('Seleção de fornecedor realizada com sucesso!');
    setTimeout(() => setSuccessMessage(undefined), 1000);
  }

  const hasRole = role === 'Master' || role === 'Admin - Comercial' || role === 'Admin - sourcing';

  const { api, paths } = useApi();

  async function loadContacts(personId: number) {
    try {
      setIsLoadingContacts(true);

      const { data } = await api.get<ContactOption[]>(paths.contact.listContact, {
        params: { personId },
        headers: { userFunctionKey: 'd82c8d1619ad8176d665453cfb2e55f0' }
      });

      setEmailOptions(data);
      setSuccessMessage('Lista de contatos carregadas!');
      setTimeout(() => setSuccessMessage(undefined), 1000);
    } catch {
      selectedFailedMessage('Falha ao carregar contatos!');
      setTimeout(() => selectedFailedMessage(undefined), 1000);
      Log.error('Erro ao carregar dados!');
    } finally {
      setIsLoadingContacts(false);
    }
  }

  useEffect(() => {
    loadContacts(personId);
  }, [personId]);

  const handleChangeTitle: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setErrorMessage((prev) => ({ ...prev, title: undefined }));
    setTitle(e.target.value);
  };

  const handleChangeContent: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setErrorMessage((prev) => ({ ...prev, content: undefined }));
    setContent(e.target.value);
  };

  const handleChangeFileList: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setFileList((prev) => {
      if (!e.target.files) return prev;

      if (!prev) return Array.from(e.target.files);

      return Array.from(prev).concat(Array.from(e.target.files));
    });
  };

  useEffect(() => {
    if (!fileList && inputRef.current) {
      inputRef.current.files = null;
      inputRef.current.value = '';
    }
  }, [fileList]);

  function handleChangeMultiple(event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) {
    const { value } = event.target;
    const groups = value as number[];

    setSelectContact(() => {
      if (groups.includes(0)) return groups;

      return [0, ...(value as number[])];
    });
  }

  function removeOption(option: number) {
    if (option === 0) return;

    setSelectContact((prev) => prev.filter((item) => item !== option));
  }

  async function handleClickSendMail() {
    const hasTitle = !!title;
    const hasContent = !!content;

    if (!hasTitle || !hasContent) {
      setErrorMessage({
        title: !hasTitle ? 'Coloque um assunto' : undefined,
        content: !hasContent ? 'Coloque uma mensagem' : undefined
      });
      return;
    }
    const formData = new FormData();

    formData.append('title', title);
    formData.append('content', content);
    formData.append('personId', personId.toString());

    selectedContact.forEach((group) => {
      if (group) formData.append('groups', group.toString());
    });

    if (fileList) {
      for (let index = 0; index < fileList.length; index++) {
        const file = fileList[index];

        if (file) formData.append('files', file);
      }
    }

    try {
      setSendingEmail(true);
      await api.postForm(paths.contact.sendMail, formData, {
        headers: { userFunctionKey: 'd82c8d1619ad8176d665453cfb2e55f0' }
      });

      setSuccessMessage('E-mail enviado com sucesso!');
      setTimeout(() => setSuccessMessage(undefined), 2000);

      setTitle('');
      setContent('');
      setFileList(null);
      setSelectContact([0]);
    } catch (error) {
      console.error(error);
      selectedFailedMessage('Erro ao enviar e-mail');
      setTimeout(() => selectedFailedMessage(undefined), 2000);
    } finally {
      setSendingEmail(false);
    }
  }

  const filesByteCount = React.useMemo(() => {
    if (!fileList || fileList.length === 0) return;

    const acceptFilesExtensions = acceptedFileTypes.split(',').map((extenstion) => extenstion.trim());

    let count = 0;
    let invalidFilesCount = 0;
    for (let index = 0; index < fileList.length; index++) {
      const file = fileList[index];

      if (file) {
        count += file.size;
        if (acceptFilesExtensions.every((extenstion) => !file.type.includes(extenstion))) {
          console.log('invalid file', file);
          invalidFilesCount += 1;
        }
      }
    }

    if (count > tenMegaBytes) {
      setErrorMessage((prev) => ({ ...prev, file: 'Arquivos muito grandes (devem ser menores de 10MB)' }));
    } else if (invalidFilesCount > 0) {
      const message = `Alguns arquivos são inválidos, use arquivos (${acceptedFiles})`;
      setErrorMessage((prev) => ({ ...prev, file: message }));
    } else {
      setErrorMessage((prev) => ({ ...prev, file: undefined }));
    }

    return byteFormater.format(count);
  }, [fileList]);

  const isDisabledButton = React.useMemo(() => {
    const errorMessageCount = Object.entries(errorMessage).filter(([_, value]) => value).length;
    return errorMessageCount > 0;
  }, [errorMessage]);

  function findGroupNameFromCode(cdGep: string) {
    return emailOptions.find((item) => Number(item.cdGep) === Number(cdGep));
  }

  function getLabelFromCode(cdGep: number) {
    if (cdGep === 0) return 'Equipe Tok&Stok';

    const group = findGroupNameFromCode(cdGep.toString());

    return group ? formatContactLabel(group) : '';
  }

  function formatSelectedGroups(selected: string[]) {
    const selectedContactList = selected
      .map(findGroupNameFromCode)
      .filter(Boolean)
      .map((selectedItem) => (selectedItem ? formatContactLabel(selectedItem) : ''));

    return ['Equipe Tok&Stok', ...selectedContactList].join(', ');
  }

  function formatFileName(file: File) {
    return `${file.name} (${byteFormater.format(file.size)})`;
  }

  function removeFile(file: File, index: number) {
    setFileList((prev) => {
      if (!prev) return null;

      return prev.filter((itemFile, fileIndex) => itemFile.name !== file.name && fileIndex !== index);
    });
  }

  const emailOptionsList = useMemo(() => {
    return [{ cdGep: 0, nmGep: '' }, ...emailOptions] as ContactOption[];
  }, [emailOptions]);

  return (
    <>
      <Helmet>
        <title>{`${process.env.REACT_APP_TITLE} - Contatos`}</title>
      </Helmet>
      <Cabecario title="Contato" />
      <div className="container contact">
        {hasRole && <Filters onChange={handleChangeSupplier} />}

        <FormControl>
          <InputLabel>E-mail para:</InputLabel>
          <Select
            multiple
            input={<Input />}
            value={selectedContact}
            onChange={handleChangeMultiple}
            className="multiselectContainer"
            classes={{ root: 'multiselect', selectMenu: 'selectLabel' }}
            renderValue={(selected) => formatSelectedGroups(selected as string[])}
          >
            {isLoadingContacts ? (
              <CircularProgress />
            ) : (
              emailOptionsList.map((item) => (
                <MenuItem key={item.cdGep} value={item.cdGep}>
                  {formatContactLabel(item)}
                </MenuItem>
              ))
            )}
          </Select>
          <List className="selectList">
            {selectedContact.sort().map((group) => (
              <Chip
                key={group}
                variant="outlined"
                label={getLabelFromCode(group)}
                onDelete={() => removeOption(group)}
              />
            ))}
          </List>
        </FormControl>

        <FormControl error={!!errorMessage.title}>
          <InputLabel>Assunto do e-mail</InputLabel>
          <Input value={title} className="titleInput" onChange={handleChangeTitle} inputProps={{ maxLength: 150 }} />
          <FormHelperText>{errorMessage.title}</FormHelperText>
        </FormControl>

        <FormControl error={!!errorMessage.content}>
          <InputLabel>Conteúdo do e-mail</InputLabel>
          <Input
            multiline
            minRows={5}
            maxRows={5}
            value={content}
            className="textarea"
            onChange={handleChangeContent}
            classes={{ input: 'input' }}
            inputProps={{ maxLength: contentMaxLength }}
          />
          <div className="textareaFooter">
            <FormHelperText>{errorMessage.content}</FormHelperText>
            <Typography className="textareaTextCount">{contentMaxLength - content.length}</Typography>
          </div>
        </FormControl>

        <FormControl error={!!errorMessage.file}>
          <div className="fileUpload">
            <input
              multiple
              type="file"
              ref={inputRef}
              accept={acceptedFiles}
              id="contact-upload-file"
              onChange={handleChangeFileList}
            />
            <label htmlFor="contact-upload-file" className="button">
              <Button color="primary" component="span" variant="contained" startIcon={<CloudUpload />}>
                Adicionar Arquivos
              </Button>
            </label>
            {fileList && fileList.length > 0 && (
              <div className="fileContent">
                <div className="fileContentText">
                  <Typography display="inline">
                    {fileList.length} arquivos ({filesByteCount}):&nbsp;
                  </Typography>
                  <Box display="flex" gridGap={2} flexWrap="wrap" flexGrow={0}>
                    {fileList.map((file, index) => (
                      <Chip
                        component="button"
                        label={formatFileName(file)}
                        key={`${file.name}-${index}`}
                        onDelete={() => removeFile(file, index)}
                      />
                    ))}
                  </Box>
                </div>
              </div>
            )}
          </div>
          <FormHelperText>{errorMessage.file}</FormHelperText>
        </FormControl>

        <Button
          color="primary"
          className="button"
          variant="contained"
          onClick={handleClickSendMail}
          disabled={isDisabledButton || isSendingEmail || isLoadingContacts}
        >
          Enviar e-mail
        </Button>
        {successMessage && (
          <Alert variant="filled" severity="success">
            {successMessage}
          </Alert>
        )}
        {failedMessage && (
          <Alert variant="filled" severity="error">
            {failedMessage}
          </Alert>
        )}
      </div>
    </>
  );
}
